/** @app — application layer; owned per-fork (LMS / OTT). See PLATFORM_FILES.md. */
//
// Certificate badge — the single source of truth for the "this course is
// certified" pill. Styled to match the course-detail page's certificate
// chip, which is a shadcn <Badge variant="secondary"> (neutral grey pill,
// no icon). Self-contained via inline styles so it can be reused outside
// the portal's Tailwind/shadcn scope (e.g. site-builder course cards),
// while still consuming the same design tokens for an exact visual match.
import type { CSSProperties } from 'react';

/**
 * How the certificate marker renders:
 * - {@code 'badge'} — the badge image only (falls back to the text pill
 *   when no {@code badgeUrl} is set, so it is never empty).
 * - {@code 'text'} — the text pill only ("Sertifika"), image ignored.
 * - {@code 'both'} — the badge image and the text pill side by side.
 */
export type CertificateBadgeMode = 'badge' | 'text' | 'both';

interface CertificateBadgeProps {
  /** Visible label, e.g. the translated "Sertifika". */
  label: string;
  /** Optional custom badge image. */
  badgeUrl?: string;
  /** Which parts to render. Omit for legacy behavior (image when
   *  {@code badgeUrl} is present, otherwise the text pill). */
  mode?: CertificateBadgeMode;
  /** Extra styles merged onto the outer element. */
  style?: CSSProperties;
}

export function CertificateBadge({ label, badgeUrl, mode, style }: CertificateBadgeProps) {
  // Legacy default preserves the original behavior for callers that don't
  // opt into a mode (e.g. the course-detail page): image when a badge URL
  // exists, otherwise the text pill.
  const effectiveMode: CertificateBadgeMode = mode ?? (badgeUrl ? 'badge' : 'text');

  const showImage = Boolean(badgeUrl) && effectiveMode !== 'text';
  // 'badge' with no image would render nothing — fall back to the pill.
  const showText =
    effectiveMode === 'text' ||
    effectiveMode === 'both' ||
    (effectiveMode === 'badge' && !badgeUrl);

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        alignSelf: 'flex-start',
        ...style,
      }}
    >
      {showImage && (
        <img
          src={badgeUrl}
          alt={label}
          title={label}
          style={{ height: 28, objectFit: 'contain', display: 'block' }}
        />
      )}
      {showText && (
        // Mirrors shadcn `Badge variant="secondary"`:
        // h-5 · px-2 py-0.5 · rounded-4xl · text-xs · font-medium ·
        // bg-secondary · text-secondary-foreground · border-transparent
        <span
          title={label}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
            height: 20,
            padding: '2px 8px',
            borderRadius: 'var(--radius-4xl, 999px)',
            fontSize: 'var(--font-size-xs, 12px)',
            fontWeight: 500,
            lineHeight: 1.4,
            whiteSpace: 'nowrap',
            background: 'var(--color-secondary, var(--secondary, #ececec))',
            color: 'var(--color-secondary-foreground, var(--secondary-foreground, #1c1d1f))',
            border: '1px solid transparent',
          }}
        >
          {label}
        </span>
      )}
    </span>
  );
}
